/**
 * Help Center (#1508).
 *
 * Replaces the bare "Coming soon" stub that every user reached from the
 * Help nav item. Content is deliberately universal + accurate — it links
 * only to rails that exist, documents the real keyboard shortcuts from
 * SHORTCUT_CATALOG, and describes actual product behaviour (citations,
 * faithfulness verification, ask-when-unsure, classification-aware
 * answers). Quick-start links are context-aware: tenant users see the
 * tenant rails, sysadmins see the platform rails, so no link ever
 * dead-ends. No backend, no mock data.
 */

import type { ReactNode } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  HelpCircle, MessageSquareText, BookOpen, Bot, CheckSquare, Workflow,
  ShieldCheck, Building2, Boxes, Activity, ServerCog, Users, Keyboard,
  Quote, ScanSearch, HelpingHand, Lock, LifeBuoy,
} from 'lucide-react';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import { useAuth } from '@/contexts/auth-context';
import { SHORTCUT_CATALOG, type KeyboardShortcutEntry } from '@/lib/use-keyboard-shortcuts';

export const Route = createFileRoute('/help')({
  component: HelpPage,
});

interface QuickLink {
  title: string;
  description: string;
  to: string;
  icon: typeof MessageSquareText;
}

const TENANT_LINKS: QuickLink[] = [
  { title: 'Ask Argus', description: 'Ask questions and get cited, verified answers from your knowledge and live data.', to: '/ask-argus', icon: MessageSquareText },
  { title: 'Library', description: 'Upload and organise documents, manuals, and SOPs for Argus to draw on.', to: '/library', icon: BookOpen },
  { title: 'Agents', description: 'Use and build specialist agents for maintenance, safety, reliability, and more.', to: '/agents', icon: Bot },
  { title: 'Approvals', description: 'Review and approve actions that require a human in the loop before they run.', to: '/approvals', icon: CheckSquare },
  { title: 'Automate', description: 'Compose scheduled and event-triggered workflows from agents and tools.', to: '/automate', icon: Workflow },
  { title: 'Trust', description: 'See governance, audit history, data flows, and compliance posture.', to: '/trust', icon: ShieldCheck },
];

const SYSADMIN_LINKS: QuickLink[] = [
  { title: 'Tenants', description: 'Provision, configure, suspend, and inspect tenant organisations.', to: '/sysadmin/tenants', icon: Building2 },
  { title: 'Models', description: 'Manage the model catalog, engines, assignments, and change history.', to: '/sysadmin/models', icon: Boxes },
  { title: 'Activity', description: 'Live cross-tenant stream of platform-scoped audit events.', to: '/sysadmin/audit/stream', icon: Activity },
  { title: 'Runtime', description: 'Inspect inference, hardware, queues, storage, and service health.', to: '/sysadmin/runtime', icon: ServerCog },
  { title: 'Administrators', description: 'Manage platform administrators and their sessions.', to: '/sysadmin/admins', icon: Users },
  { title: 'Platform', description: 'Air-gap posture, backups, keys, license, and updates.', to: '/sysadmin/platform', icon: ShieldCheck },
];

// How Argus answers — the real founding behaviours (CLAUDE.md §1, §11).
const HOW_IT_WORKS = [
  { title: 'Answers are cited', description: 'Every claim points back to the document, page, and section it came from — open the citation to verify the source.', icon: Quote },
  { title: 'Claims are verified', description: 'Generated statements are checked against their cited source (faithfulness verification) before you see them; unsupported claims are flagged or removed.', icon: ScanSearch },
  { title: 'It asks when unsure', description: 'Rather than guess on ambiguous questions, Argus asks a clarifying question with options — truth over speed.', icon: HelpingHand },
  { title: 'Classification-aware', description: 'Responses inherit the highest classification of their sources, and export-controlled material is gated before it ever reaches the model.', icon: Lock },
];

// Render a shortcut entry as an ordered list of key tokens.
function shortcutKeys(s: KeyboardShortcutEntry): string[] {
  const keys: string[] = [];
  if (s.mod) keys.push('⌘ / Ctrl');
  if (s.shift) keys.push('Shift');
  keys.push(s.key.length === 1 ? s.key.toUpperCase() : s.key);
  return keys;
}

const SHORTCUT_CATEGORIES: KeyboardShortcutEntry['category'][] = ['Navigation', 'Composer', 'Thread'];

function HelpPage() {
  const { isSysadmin } = useAuth();
  const quickLinks = isSysadmin ? SYSADMIN_LINKS : TENANT_LINKS;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-8">
      <div className="flex items-center gap-3">
        <HelpCircle className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Help Center</h1>
          <p className="text-muted-foreground">
            Find your way around ArgusAI, learn how answers are produced, and get support.
          </p>
        </div>
      </div>

      {/* Quick start */}
      <section className="space-y-3">
        <h2 className="text-lg font-semibold">Quick start</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {quickLinks.map((l) => {
            const Icon = l.icon;
            return (
              <Link key={l.to} to={l.to} className="group">
                <Card className="h-full transition-colors hover:border-primary/50 hover:bg-accent/40">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Icon className="h-4 w-4 text-primary" />
                      {l.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{l.description}</p>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      </section>

      {/* How Argus answers — only meaningful for tenant users */}
      {!isSysadmin && (
        <section className="space-y-3">
          <h2 className="text-lg font-semibold">How Argus answers</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {HOW_IT_WORKS.map((h) => {
              const Icon = h.icon;
              return (
                <Card key={h.title}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Icon className="h-4 w-4 text-primary" />
                      {h.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{h.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>
      )}

      {/* Keyboard shortcuts — Ask Argus chat shortcuts; tenant-relevant */}
      {!isSysadmin && (
        <section className="space-y-3">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Keyboard className="h-5 w-5 text-muted-foreground" />
            Keyboard shortcuts
          </h2>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>
                Available in the Ask Argus chat. Press <Kbd>?</Kbd> anywhere in chat to open this list.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {SHORTCUT_CATEGORIES.map((cat) => {
                const rows = SHORTCUT_CATALOG.filter((s) => s.category === cat);
                if (rows.length === 0) return null;
                return (
                  <div key={cat}>
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                      {cat}
                    </div>
                    <div className="space-y-1.5">
                      {rows.map((s, i) => (
                        <div key={`${s.label}-${i}`} className="flex items-center justify-between gap-4 text-sm">
                          <span className="text-muted-foreground">{s.label}</span>
                          <span className="flex items-center gap-1 shrink-0">
                            {shortcutKeys(s).map((k, j) => (
                              <span key={j} className="flex items-center gap-1">
                                {j > 0 && <span className="text-muted-foreground text-xs">+</span>}
                                <Kbd>{k}</Kbd>
                              </span>
                            ))}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </section>
      )}

      {/* Support */}
      <section className="space-y-3">
        <h2 className="text-lg font-semibold">Need more help?</h2>
        <Card>
          <CardContent className="py-5 flex items-start gap-3">
            <LifeBuoy className="h-5 w-5 text-primary mt-0.5 shrink-0" />
            <div className="text-sm text-muted-foreground space-y-1">
              {isSysadmin ? (
                <>
                  <p>
                    Platform operations, deployment, and air-gap procedures are documented in
                    the operator runbooks shipped with your installation.
                  </p>
                  <p>
                    For tenant-level questions, work with the affected tenant&apos;s administrator
                    from the <Link to="/sysadmin/tenants" className="text-primary hover:underline">Tenants</Link> rail.
                  </p>
                </>
              ) : (
                <>
                  <p>
                    For access, permissions, new connections, or feature requests, contact your
                    organisation&apos;s ArgusAI administrator.
                  </p>
                  <p>
                    Reviewing the audit trail and governance posture? See the{' '}
                    <Link to="/trust" className="text-primary hover:underline">Trust</Link> rail.
                  </p>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}

// Small inline keycap. Local to this page — the only consumer.
function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="inline-flex items-center rounded border border-border bg-muted px-1.5 py-0.5 text-xs font-mono text-foreground">
      {children}
    </kbd>
  );
}
